import {
  useEffect, useRef, useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { useTranslation } from 'react-i18next';

import { Mixpanel } from 'mixpanel';
import Button from 'components/Button';
import microsoftLoginMutation from 'api/mutations/microsoftLoginMutation';
import { queryClient } from 'utils/queryClient';
import { agent } from 'api/agent';

import logo from '/logo.svg';

import Loading from 'components/Loading';
import { getMicrosoftRedirectUri } from 'utils/auth';
import { useAppContext } from 'store/hooks';
import SecureStorage from 'utils/localStorage';
import { isOmya } from 'utils/isOmya';

type OrganizationType = 'omya' | 'distributor' | null;

interface MicrosoftConfig {
  clientId: string;
  tenantId: string;
}

const ORG_TYPE_STORAGE_KEY = 'ssoOrganizationType';

const isValidOrgType = (value: string | null): value is 'omya' | 'distributor' => value === 'omya' || value === 'distributor';

const getMicrosoftConfig = (orgType: OrganizationType): MicrosoftConfig | null => {
  if (orgType === 'omya') {
    return {
      clientId: import.meta.env.VITE_APP_MICROSOFT_CLIENT_ID,
      tenantId: import.meta.env.VITE_APP_MICROSOFT_TENANT_ID,
    };
  }

  if (orgType === 'distributor') {
    return {
      clientId: import.meta.env.VITE_APP_MICROSOFT_DISTRIBUTOR_CLIENT_ID,
      tenantId: import.meta.env.VITE_APP_MICROSOFT_DISTRIBUTOR_TENANT_ID,
    };
  }

  return null;
};

const loginRequest = {
  scopes: ['user.read', 'openid', 'profile', 'offline_access'],
  prompt: 'select_account',
};

const createMSALInstance = (config: MicrosoftConfig): PublicClientApplication => {
  const msalConfig = {
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: getMicrosoftRedirectUri(),
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage' as const,
      storeAuthStateInCookie: true,
    },
  };

  return new PublicClientApplication(msalConfig);
};

const LoginOmya = () => {
  const reactNativeWebView = (window as any)?.ReactNativeWebView;
  const navigate = useNavigate();
  const { ssoAccessToken } = useAppContext();
  const [searchParams] = useSearchParams();

  const { t } = useTranslation();

  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationType>(null);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = microsoftLoginMutation();

  const redirectHandled = useRef(false);

  useEffect(() => {
    if (reactNativeWebView || redirectHandled.current) {
      return;
    }

    redirectHandled.current = true;

    const orgTypeFromUrl = searchParams.get('org');
    const storedOrgType = sessionStorage.getItem(ORG_TYPE_STORAGE_KEY);
    const orgType = isValidOrgType(storedOrgType)
      ? storedOrgType
      : isValidOrgType(orgTypeFromUrl) ? orgTypeFromUrl : null;

    if (orgType) {
      setSelectedOrganization(orgType);
      handleRedirectReturn(orgType);
    }
  }, []);

  const handleRedirectReturn = async (orgType: OrganizationType) => {
    if (!orgType) {
      return;
    }

    const config = getMicrosoftConfig(orgType);

    if (!config) {
      sessionStorage.removeItem(ORG_TYPE_STORAGE_KEY);
      setError('Configuration error');
      setSelectedOrganization(null);
      navigate('/auth/login-omya', { replace: true });

      return;
    }

    try {
      const pca = createMSALInstance(config);
      await pca.initialize();
      const redirectResult = await pca.handleRedirectPromise();

      if (redirectResult?.accessToken) {
        sendUser(redirectResult.accessToken);
      } else {
        navigate('/auth/login-omya', { replace: true });
        setSelectedOrganization(null);
      }
    } catch (err) {
      console.error('MSAL redirect error:', err);
      setError('Authentication failed');
      navigate('/auth/login-omya', { replace: true });
      setSelectedOrganization(null);
    } finally {
      sessionStorage.removeItem(ORG_TYPE_STORAGE_KEY);
    }
  };

  const handleOrganizationSelect = async (orgType: 'omya' | 'distributor') => {
    setError(null);
    setSelectedOrganization(orgType);

    const config = getMicrosoftConfig(orgType);

    if (!config) {
      setError('Configuration error');
      setSelectedOrganization(null);

      return;
    }

    navigate(`/auth/login-omya?org=${orgType}`, { replace: true });

    try {
      const pca = createMSALInstance(config);

      await pca.initialize();
      setInitialized(true);

      if (reactNativeWebView) {
        reactNativeWebView.postMessage(JSON.stringify({
          type: orgType === 'distributor' ? 'microsoft-login-distributor' : 'microsoft-login',
        }));
      } else {
        sessionStorage.setItem(ORG_TYPE_STORAGE_KEY, orgType);
        await pca.loginRedirect(loginRequest);
      }
    } catch (err) {
      sessionStorage.removeItem(ORG_TYPE_STORAGE_KEY);
      console.error('MSAL initialization error:', err);
      setError('Initialization failed');
      setSelectedOrganization(null);
      navigate('/auth/login-omya', { replace: true });
    }
  };

  const sendUser = (accessToken: string) => {
    mutate(accessToken, {
      onSuccess(res) {
        if (isOmya(res.data?.user?.organization)) {
          Mixpanel.identify(res.data?.user?.email);
          Mixpanel.track('Sign In (misrosoft)', {
            email: res.data?.user?.email,
            role: res.data?.user?.role?.name,
            organization: res.data?.user?.organization ?? 'No organization',
          });
        } else {
          Mixpanel.identify();
          Mixpanel.track('Sign In (misrosoft)', {
            role: res.data?.user?.role?.name,
            organization: res.data?.user?.organization ?? 'No organization',
          });
        }

        if (res.data?.user) {
          queryClient.setQueryData(['user'], res.data?.user);
        }

        SecureStorage.setItem('token', res.data.token, { secure: true });
        SecureStorage.setItem('refreshToken', res.data.refreshToken, { secure: true });
        SecureStorage.setItem('ssoLogin', 'true');
        agent.getClient().defaults.headers.common[
          'Authorization'
        ] = `Bearer ${res.data.token}`;

        navigate('/');
      },
      onError(err: any) {
        if (err?.response?.data?.errors?.email) {
          setError(err?.response?.data?.errors?.email);
        }
      },
    });
  };

  /* Mobile Login */
  useEffect(() => {
    if (!reactNativeWebView) {
      return;
    }

    const onInitMobileApp = async () => {
      if (ssoAccessToken) {
        sendUser(ssoAccessToken);
      }
    };

    onInitMobileApp();
  }, [ssoAccessToken]);

  if (!selectedOrganization) {
    return (
      <div className="flex flex-col w-full items-center justify-center max-h-screen">
        <div className="flex flex-col border py-[60px] px-[40px] rounded-2xl border-[#CBCBCB] w-[480px] bg-[#fff] md:w-[358px]">
          <div className="justify-center items-center flex">
            <img src={logo} alt="logo" />
          </div>
          <div className="text-3xl font-bold my-10 md:my-6 justify-center items-center flex md:text-2xl">
            {t('welcomeToEvs')}
          </div>
          <div className="flex justify-center mb-[16px]">
            <Button
              onClick={() => handleOrganizationSelect('omya')}
              className="md:py-4 md:px-6"
            >
              {t('signInAsOmyaUser')}
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="h-px bg-[#E2E2E2] w-1/3 mx-3" />
            <div className="text-base text-[#757575] justify-center items-center flex">
              {t('or')}
            </div>
            <div className="h-px bg-[#E2E2E2] w-1/3 mx-3" />
          </div>
          <div className="flex justify-center mt-[16px]">
            <Button
              onClick={() => handleOrganizationSelect('distributor')}
              btnType="secondary"
              className="md:py-4 md:px-6"
            >
              {t('signInAsDistributor')}
            </Button>
          </div>
          {error && (
            <div className="text-[#FF3B30] text-xs mt-4 text-center">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full items-center justify-center max-h-screen">
      <div className="flex flex-col border py-[60px] px-[40px] rounded-2xl border-[#CBCBCB] w-[480px] bg-[#fff] md:w-[358px]">
        <div className="justify-center items-center flex">
          <img src={logo} alt="logo" />
        </div>
        <div className="text-3xl font-bold my-10 md:my-6 justify-center items-center flex md:text-2xl">
          {t('welcomeToEvs')}
        </div>
        <div className="flex justify-center mb-[16px]">
          <Button
            className="md:py-4 md:px-6"
            disabled={true}
          >
            {isPending || !initialized ? <Loading size={25} /> : t('signinwithMocrosoft')}
          </Button>
          {error && (
            <span className="absolute bottom-[-3px] text-[#FF3B30] text-xs ml-4 md:bottom-[-16px]">
              {error}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginOmya;
